import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after 3 seconds
      setTimeout(() => {
        setShowInstallBanner(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS instructions after delay
    if (isIOSDevice) {
      const hasSeenIOSPrompt = localStorage.getItem('excelsium-ios-prompt-seen');
      if (!hasSeenIOSPrompt) {
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      localStorage.setItem('excelsium-ios-prompt-seen', 'true');
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    setShowIOSInstructions(false);
    if (isIOS) {
      localStorage.setItem('excelsium-ios-prompt-seen', 'true');
    }
  };

  if (!showInstallBanner && !showIOSInstructions) return null;

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ios-install-modal">
        <div className="bg-white max-w-sm w-full p-6 rounded-lg shadow-xl">
          <div className="flex justify-between items-start mb-4">
            <h3 className="font-serif text-xl text-[#1A1A1A]">Installer l'application</h3>
            <button onClick={dismissBanner} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4 text-gray-600 text-sm">
            <p>Pour installer Excelsium Conseils sur votre iPhone/iPad :</p>
            <ol className="list-decimal list-inside space-y-2">
              <li>Appuyez sur le bouton <strong>Partager</strong> <span className="inline-block w-5 h-5 align-middle">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .9 2 2z"/></svg>
              </span> en bas de Safari</li>
              <li>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
              <li>Appuyez sur <strong>"Ajouter"</strong></li>
            </ol>
          </div>
          <button
            onClick={dismissBanner}
            className="w-full mt-6 py-3 bg-[#C9A227] text-white font-medium hover:bg-[#b08d1f] transition-colors"
          >
            Compris
          </button>
        </div>
      </div>
    );
  }

  // Install Banner
  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-[#1A1A1A] text-white p-4 rounded-lg shadow-xl z-40 animate-fade-in"
      data-testid="install-pwa-banner"
    >
      <button 
        onClick={dismissBanner}
        className="absolute top-2 right-2 text-white/60 hover:text-white"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-[#C9A227] rounded-lg flex items-center justify-center flex-shrink-0">
          <Download className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-sm mb-1">Installer Excelsium Conseils</p>
          <p className="text-white/60 text-xs mb-3">Accédez rapidement à nos services depuis votre écran d'accueil</p>
          <button
            onClick={handleInstallClick}
            className="w-full py-2 bg-[#C9A227] text-white text-sm font-medium rounded hover:bg-[#b08d1f] transition-colors"
          >
            {isIOS ? "Comment installer" : "Installer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
